import { EFinancingProgram, EMerchantType } from '@if/core/types'
import { Chip, CircularProgress, Grid, Link, Stack, Typography } from '@mui/material'
import { FormatPromoCode, FormatPromoCodeWithRates } from '@src/containers/App/utils/Formatter'
import useValueList from '@src/data/api/config-api'
import useFeatureFlags from '@src/data/api/featureSwitch-api/featureSwitch-api'
import { useGetMerchantPaymentPlans, useMerchantById } from '@src/data/api/merchants-api/merchants-api'
import { useTerritoryById } from '@src/data/api/territories-api/territories-api'
import { useRateAgreementsByFinancingProgram } from '@src/data/hooks/merchant-hooks'
import { EValueList } from '@src/data/types/Constants'
import { Merchant } from '@src/data/types/Merchant'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'
import RateAgreementsTable from './RateAgreementsTable'

const PageSection = ({ children }: { children: ReactNode }) => {
  return (
    <Grid container spacing={2} size={12} marginBottom={2}>
      {children}
    </Grid>
  )
}

const SectionTile = ({ title }: { title: string }) => {
  return (
    <Grid size={12}>
      <Typography variant="h6" component="div">
        {title}
      </Typography>
    </Grid>
  )
}

const PropValue = ({ label, value }: { label: string; value: ReactNode }) => {
  return (
    <>
      <Grid size={{ xs: 6, md: 3 }}>
        <Typography color="text.secondary">{label}</Typography>
      </Grid>
      <Grid size={{ xs: 6, md: 3 }}>
        <Typography>{value || ''}</Typography>
      </Grid>
    </>
  )
}

interface Props {
  merchant: Merchant
}

const MerchantInfoTab = ({ merchant }: Props) => {
  const { t } = useTranslation()
  const [featureFlags] = useFeatureFlags()
  const [tags, isLoadingTags] = useValueList(EValueList.Tags)
  const [territory, isLoadingTerritory] = useTerritoryById(merchant.territoryId)
  const [banner, isLoadingBanner] = useMerchantById(
    merchant.parentId && merchant.parentId !== 'monolith*0' ? merchant.parentId : '',
  )

  const YES = t('common.yes')
  const NO = t('common.no')
  const merchantType =
    merchant.merchantTypeId === EMerchantType.Merchant ? t('common.serviceProvider') : t('common.banner')
  const groupedRateAgreements = useRateAgreementsByFinancingProgram(merchant.rateAgreements)
  const [merchantPaymentPlans] = useGetMerchantPaymentPlans(merchant.id)

  if (isLoadingTags || isLoadingTerritory || isLoadingBanner) {
    return (
      <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} padding={2}>
        <CircularProgress />
      </Stack>
    )
  }

  return (
    <Stack direction="row" justifyContent="center" alignItems="flex-start" spacing={2} padding={2}>
      <Grid container spacing={2}>
        <PageSection>
          <SectionTile title={t('common.generalInfo')} />

          <PropValue label={t('merchant.isActive')} value={merchant.isActive ? YES : NO} />
          <PropValue label={t('common.email')} value={merchant.email} />
          <PropValue label={t('common.website')} value={merchant.website} />
          <PropValue label={t('merchant.type')} value={merchantType} />
          <PropValue
            label="Parent"
            value={
              banner ? (
                <Link component={RouterLink} to={`/merchant/${banner.id}/view`}>
                  {banner.name}
                </Link>
              ) : (
                ''
              )
            }
          />
          <PropValue
            label={t('merchant.vertical')}
            value={merchant.serviceCategory ? t(`ELoanPurpose.${merchant.serviceCategory}`, { ns: 'enum' }) : ''}
          />
          <PropValue label={t('common.territory')} value={territory?.name ?? t('merchant.noTerritory')} />
          <PropValue
            label={t('merchant.supportedFinancingPrograms')}
            value={merchant.supportedFinancingPrograms
              ?.map((p) => t(`EFinancingProgram.${p}`, { ns: 'enum' }))
              .join(', ')}
          />
          <PropValue label={t('merchant.supportedPaymentPlans')} value={merchant.supportedPaymentPlans?.join(', ')} />
        </PageSection>

        <PageSection>
          <SectionTile title={t('merchant.paymentInfo')} />
          <PropValue
            label={t('merchant.paymentInstructions')}
            value={merchant.paymentInstructions || t('merchant.noPaymentInstructions')}
          />

          <PropValue label={t('merchant.defaultPaymentMethod')} value={merchant.defaultPaymentMethod} />
        </PageSection>

        <PageSection>
          <SectionTile title={t('common.reports')} />
          <PropValue
            label={t('merchant.reports.frequency')}
            value={
              merchant.reportGenerationFrequency
                ? t(`common.eMerchantReportFrequency.${merchant.reportGenerationFrequency}`, '', { ns: 'enum' })
                : ''
            }
          />
          <PropValue label={t('merchant.reports.recipients')} value={merchant.reportRecipientsEmailList?.join(', ')} />
        </PageSection>

        <PageSection>
          <SectionTile title={t('common.options')} />
          <PropValue label={t('merchant.noPaymentPolicy.title')} value={merchant.noPaymentPolicy ? YES : NO} />
          <PropValue
            label={t('merchant.canAcceptHighEstimate.title')}
            value={merchant.canAcceptHighEstimate ? YES : NO}
          />
          <PropValue label={t('merchant.canSkipFlinks.title')} value={merchant.canSkipFlinks ? YES : NO} />
          <PropValue label={t('merchant.canPayBorrowers.title')} value={merchant.canPayBorrowers ? YES : NO} />
          <PropValue
            label={t('merchant.forcePaymentToBorrower.title')}
            value={merchant.forcePaymentToBorrower ? YES : NO}
          />
          <PropValue
            label={t('merchant.automaticIncomeApproval.title')}
            value={merchant.automaticIncomeApproval ? YES : NO}
          />
          {featureFlags?.useMerchDBshowRate && (
            <PropValue
              label={t('merchant.showInterestRate.title')}
              value={merchant.showInterestRateOnMerchantDashboard ? YES : NO}
            />
          )}
          {featureFlags?.useHybridFlowWorksheetByMerchant && (
            <PropValue
              label={t('merchant.worksheetByMerchant.title')}
              value={merchant.worksheetByMerchant ? YES : NO}
            />
          )}
        </PageSection>

        <PageSection>
          <SectionTile title={t('merchant.tags')} />
          <Stack direction="row" gap={1} flexWrap="wrap">
            {merchant.tags?.map((tag) => <Chip key={tag} label={tags?.find((item) => item.id === tag)?.text} />)}
          </Stack>
        </PageSection>
        <PageSection>
          <SectionTile title={t('merchant.rateAgreements')} />
          {Object.entries(groupedRateAgreements).map(([financingProgramId, rateAgreements]) => {
            return (
              <RateAgreementsTable
                key={financingProgramId}
                financingProgramId={financingProgramId as EFinancingProgram}
                rateAgreements={rateAgreements}
              />
            )
          })}
        </PageSection>
        <PageSection>
          <SectionTile title={t('merchant.ratesAndFees.title')} />
          <PropValue
            label={t('merchant.ratesAndFees.adminFeeCustomer')}
            value={merchant.merchantBaseRates?.lenderFeeRate.toString()}
          />

          <PropValue
            label={t('merchant.ratesAndFees.adminFeeMerchant')}
            value={merchant.merchantBaseRates?.adminFeeMerchant.toString()}
          />

          <PropValue
            label={t('merchant.ratesAndFees.customerRateModifier')}
            value={merchant.merchantBaseRates?.interestRateModifier.toString()}
          />
        </PageSection>
        <PageSection>
          <SectionTile title={t('merchant.PromoPlans.title')} />
          <PropValue
            label={t('merchant.PromoPlans.PromotionApplied')}
            value={
              merchantPaymentPlans
                ? merchantPaymentPlans
                    .filter((plan) => !plan.expiresOn || new Date() <= new Date(plan.expiresOn))
                    .map((plan) => FormatPromoCodeWithRates(plan))
                    .join('\n')
                    .split('\n')
                    .map((line, index) => [line, <br key={index} />])
                : ''
            }
          />
        </PageSection>
        <PageSection>
          <SectionTile title={t('merchant.defaultAppliedPromotions')} />
          {merchant.planPromoRules?.map((entry) => (
            <PropValue
              key={entry.defaultPromoCode}
              label={`${t(`EFinancingProgram.${entry.financingProgramId as EFinancingProgram}`, { ns: 'enum' })} ${t(`EPlanType.${entry.planName}`, { ns: 'enum' })}`}
              value={FormatPromoCode(entry.defaultPromoCode)}
            />
          ))}
        </PageSection>
        {merchant.financingProgramConfigs?.map((config) => {
          const title: string = t('merchant.financingProgramConfigs')
          const financingProgramName = t(`EFinancingProgram.${config.financingProgramId}`, { ns: 'enum' })
          return (
            <PageSection key={config.financingProgramId}>
              <SectionTile title={`${title} - ${financingProgramName}`} />
              {Object.entries(config).map(([fieldName, fieldValue]) => {
                if (fieldName === 'financingProgramId') return null
                if (!fieldValue) return null
                return (
                  <PropValue
                    key={fieldName}
                    label={t(`financingProgramConfigs.${fieldName}`, fieldName)}
                    value={String(fieldValue)}
                  />
                )
              })}
            </PageSection>
          )
        })}
      </Grid>
    </Stack>
  )
}

export default MerchantInfoTab
